import fetchUtil from './fetchUtil';

export interface CategoryOption {
  label: string;
  value: string;
}

/**
 * Fetches student destination categories from the API
 * Excludes "News" category from the results
 */
export const fetchStudentDestinationCategories = async (): Promise<any[]> => {
  try {
    const res = await fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/stu-dest-categories?category_ncontains=News&_fields=id,category,count`,
      'GET',
      {},
      '',
      false
    );
    
    if (!res || !Array.isArray(res)) {
      return [];
    }
    
    return res;
  } catch (error) {
    console.error('Error fetching student destination categories:', error);
    return [];
  }
};

/**
 * Fetches course categories from the API and transforms them to match expected format
 * Converts course names to slug-like values (e.g., "Business Administration & Management" -> "business_administration_management")
 */
export const fetchCourseCategories = async (): Promise<CategoryOption[]> => {
  try {
    const res = await fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/study-courses`,
      'GET',
      {},
      '',
      false
    );
    
    if (!res || !Array.isArray(res)) {
      return [];
    }

    // Transform API response to match expected format
    const transformedCategories = res.map((item: any) => {
      // Convert course name to slug-like value (similar to constants format)
      // Handles: "Business Administration & Management" -> "business_administration_management"
      // Handles: "Health Care + Pharma" -> "health_care_pharma"
      const value = item.courses
        .toLowerCase()
        .replace(/&/g, '')
        .replace(/\+/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
        .trim();
      
      return {
        label: item.courses,
        value: value
      };
    });
    
    return transformedCategories;
  } catch (error) {
    console.error('Error fetching course categories:', error);
    return [];
  }
};

/**
 * Fetches coaching categories from the API and transforms them to match expected format
 * Uses Categories field as both label and value (e.g., "IELTS", "TOEFL", "PTE")
 */
export const fetchCoachingCategories = async (): Promise<CategoryOption[]> => {
  try {
    const res = await fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/coaching-categories`,
      'GET',
      {},
      '',
      false
    );
    
    if (!res || !Array.isArray(res)) {
      return [];
    }

    // Transform API response to match expected format
    const transformedCategories = res.map((item: any) => {
      // Use Categories field as both label and value
      // Values like "IELTS", "TOEFL", "PTE" match the constants format
      return {
        label: item.Categories,
        value: item.Categories
      };
    });
    
    return transformedCategories;
  } catch (error) {
    console.error('Error fetching coaching categories:', error);
    return [];
  }
};

/**
 * Fetches success stories categories from the API and transforms them to match expected format
 * Converts storyCategories to lowercase value format (e.g., "Student" -> "student", "Partner Associate" -> "partner_associate")
 */
export const fetchSuccessStoriesCategories = async (): Promise<CategoryOption[]> => {
  try {
    const res = await fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/success-stories-categories`,
      'GET',
      {},
      '',
      false
    );
    
    if (!res || !Array.isArray(res)) {
      return [];
    }

    // Transform API response to match expected format
    const transformedCategories = res.map((item: any) => {
      // Convert storyCategories to lowercase value format
      // Handles: "Student" -> "student", "Partner Associate" -> "partner_associate"
      const value = item.storyCategories
        .toLowerCase()
        .replace(/\s+/g, '_')
        .trim();
      
      return {
        label: item.storyCategories,
        value: value
      };
    });
    
    return transformedCategories;
  } catch (error) {
    console.error('Error fetching success stories categories:', error);
    return [];
  }
};


